import React, { useState, useRef, useEffect, useMemo } from 'react';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import s from './s.module.less';
import { QuillDeltaToHtmlConverter } from 'quill-delta-to-html';

const icons = Quill.import('ui/icons');
icons.undo = `<svg viewbox="0 0 18 18">
    <polygon class="ql-fill ql-stroke" points="6 10 4 12 2 10 6 10"></polygon>
    <path class="ql-stroke" d="M8.09,13.91A4.6,4.6,0,0,0,9,14,5,5,0,1,0,4,9"></path>
  </svg>`;
icons.redo = `<svg viewbox="0 0 18 18">
    <polygon class="ql-fill ql-stroke" points="12 10 14 12 16 10 12 10"></polygon>
    <path class="ql-stroke" d="M9.91,13.91A4.6,4.6,0,0,1,9,14a5,5,0,1,1,5-5"></path>
  </svg>`;

interface IProps {
    value?: string;
    onChange?: (value: string) => void;
    maxLength?: number;
    showCount?: boolean;
    cls?: string;
    height?: string;
    plcaeholder?: string;
    hideToolbar?: boolean;
    keepHtmlLabels?: boolean;
    inlineStyle?: boolean;
    // enable custom color palettes
    colorPalette?: string[];
    backgroundPalette?: string[];
    // quillRef?: React.RefObject<ReactQuill>;
}

const QuillRichText = ({
    value,
    onChange,
    maxLength = 100,
    showCount = true,
    cls,
    height = '200px',
    plcaeholder,
    hideToolbar = false,
    keepHtmlLabels,
    inlineStyle,
    colorPalette,
    backgroundPalette,
    // quillRef,
}: IProps) => {
    const quillEditor = useRef<ReactQuill>(null);
    const [editorState, setEditorState] = useState(''); // 跟踪内容

    useEffect(() => {
        setEditorState(value || '');
    }, [value]);

    const handleChange = (content) => {
        // if (inlineStyle && quillEditor.current) {
        //     const converter = new QuillDeltaToHtmlConverter(quillEditor.current.getContents()?.ops, {
        //         inlineStyles: true,
        //     });
        //     const html = converter.convert();
        //     onChange?.(html);
        // } else
        if (keepHtmlLabels) {
            onChange?.(content);
        } else {
            const valueRemovedHTML = content.replace(/<[^>]*>/g, ''); // 移除 HTML 标签
            // if (valueRemovedHTML.length <= maxLength) {
            const resultValue = valueRemovedHTML.length === 0 ? '' : content;
            setEditorState(resultValue);
            onChange?.(resultValue);
        }
        // 若超过最大字符限制，不更新内容
        // }
    };

    // 计算已输入字符数（忽略 HTML 标签）
    const getPlainTextLength = (content) => {
        const plainText = content.replace(/<[^>]*>/g, ''); // 移除 HTML 标签
        return plainText.length;
    };

    // default palettes if not provided
    const defaultColors = useMemo(
        () => [
            '#000000',
            '#434343',
            '#666666',
            '#999999',
            '#b7b7b7',
            '#cccccc',
            '#d9d9d9',
            '#efefef',
            '#f3f3f3',
            '#ffffff',
            '#0E62F4',
            '#FFDB3B',
            '#980000',
            '#ff0000',
            '#ff9900',
            '#ffff00',
            '#00ff00',
            '#00ffff',
            '#4a86e8',
            '#0000ff',
            '#9900ff',
            '#ff00ff',
        ],
        [],
    );

    useEffect(() => {
        // register color/background style attributors with optional whitelists
        const ColorStyle = Quill.import('attributors/style/color');
        const BackgroundStyle = Quill.import('attributors/style/background');
        if (colorPalette && Array.isArray(colorPalette)) {
            ColorStyle.whitelist = colorPalette;
        } else {
            ColorStyle.whitelist = defaultColors;
        }
        if (backgroundPalette && Array.isArray(backgroundPalette)) {
            BackgroundStyle.whitelist = backgroundPalette;
        } else {
            BackgroundStyle.whitelist = defaultColors;
        }
        Quill.register(ColorStyle, true);
        Quill.register(BackgroundStyle, true);
    }, [colorPalette, backgroundPalette, defaultColors]);

    const modules = useMemo(() => {
        return {
            toolbar: hideToolbar ? false : {
                container: [
                    ['undo', 'redo'],
                    [{ size: ['small', false, 'large', 'huge'] }],
                    ['bold', 'italic'],
                    [{ color: colorPalette || defaultColors }, { background: backgroundPalette || defaultColors }],
                    [{ list: 'ordered' }, { list: 'bullet' }],
                    [{ align: [] }],
                    ['link'],
                ],
                handlers: {
                    undo: () => {
                        return quillEditor.current?.history.undo();
                    },
                    redo: () => {
                        return quillEditor.current?.history.redo();
                    },
                },
            },
            history: {
                delay: 1000,
                maxStack: 50,
            },
        };
    }, [hideToolbar, colorPalette, backgroundPalette, defaultColors]);

    return (
        <div className={`${s.wrap} ${cls}`}>
            <ReactQuill
                ref={(el) => {
                    if (el) {
                        quillEditor.current = el.getEditor();
                    }
                }}
                value={editorState}
                onChange={handleChange}
                style={{ height }}
                modules={modules}
                formats={[
                    'size', 'bold', 'italic', 'list', 'bullet', 'align', 'link', 'color', 'background', 'header',
                ]}
                placeholder={plcaeholder}
            />

            {/* 字数计数显示 */}
            {
                showCount && (
                    <div style={{
                        position: 'absolute',
                        right: '16px',
                        bottom: '4px',
                        color: 'rgba(0, 0, 0, 0.45)',
                    }}
                    >
                        {getPlainTextLength(editorState)}/{maxLength}
                    </div>
                )
            }
        </div>
    );
};

export default QuillRichText;
